import { create } from 'zustand';
import type {
  SourceModule,
  MappingStoreState,
  EnhancedFieldMapping,
  MappableProperty,
  MappingValidation,
} from '../types/mapping';
import { MAPPABLE_PROPERTIES } from '../types/mapping';
import {
  discoverSchema,
  fetchPreviewRecords,
  invalidateSchemaCache,
} from '../lib/schemaDiscovery';
import { supabase } from '../lib/supabase';
import type { CanvasElement } from '../types';
import { useStudioStore } from './studioStore';

function newMappingId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const hex = (n: number) => Math.floor(Math.random() * 16 ** n).toString(16).padStart(n, '0');
  return `${hex(8)}-${hex(4)}-4${hex(3)}-${((Math.random() * 4) | 8).toString(16)}${hex(3)}-${hex(12)}`;
}

type DbRow = {
  id: string;
  project_id: string;
  element_id: string;
  source: string;
  table_name: string;
  column_name: string;
  column_data_type: string;
  target_property: string;
  fallback_value: string;
  transform_expression: string | null;
  sort_order: number;
  created_at: string;
  filter_column: string | null;
  filter_value: string | null;
  filter_category: string | null;
};

function rowToMapping(row: DbRow): EnhancedFieldMapping {
  return {
    id: row.id,
    source: (row.source as SourceModule) || 'ad',
    tableName: row.table_name,
    columnName: row.column_name,
    columnDataType: row.column_data_type,
    targetProperty: row.target_property as MappableProperty,
    fallbackValue: row.fallback_value ?? '',
    transformExpression: row.transform_expression,
    isValid: true,
    validationErrors: [],
    createdAt: row.created_at,
    filterColumn: row.filter_column ?? null,
    filterValue: row.filter_value ?? null,
    filterCategory: row.filter_category ?? null,
  };
}

function mappingToRow(
  projectId: string,
  elementId: string,
  mapping: EnhancedFieldMapping,
  sortOrder: number
): Omit<DbRow, 'created_at'> {
  return {
    id: mapping.id,
    project_id: projectId,
    element_id: elementId,
    source: mapping.source,
    table_name: mapping.tableName,
    column_name: mapping.columnName,
    column_data_type: mapping.columnDataType,
    target_property: mapping.targetProperty,
    fallback_value: mapping.fallbackValue ?? '',
    transform_expression: mapping.transformExpression,
    sort_order: sortOrder,
    filter_column: mapping.filterColumn ?? null,
    filter_value: mapping.filterValue ?? null,
    filter_category: mapping.filterCategory ?? null,
  };
}

interface MappingStoreActions {
  loadSchema: (forceRefresh?: boolean) => Promise<void>;
  refreshSchema: () => Promise<void>;

  setCurrentProject: (projectId: string | null) => void;
  loadMappingsForProject: (
    projectId: string | null,
    projectElements?: CanvasElement[]
  ) => Promise<string[]>;

  addMapping: (
    elementId: string,
    mapping: Omit<EnhancedFieldMapping, 'id' | 'isValid' | 'validationErrors' | 'createdAt'>
  ) => void;
  addMappingToElements: (
    elementIds: string[],
    mapping: Omit<EnhancedFieldMapping, 'id' | 'isValid' | 'validationErrors' | 'createdAt'>
  ) => Promise<{ successCount: number; failureCount: number; errors: Array<{ elementId: string; error: string }> }>;
  updateMapping: (
    elementId: string,
    mappingId: string,
    updates: Partial<EnhancedFieldMapping>
  ) => void;
  removeMapping: (elementId: string, mappingId: string) => void;
  removeAllMappings: (elementId: string) => void;
  removeAllMappingsForElements: (elementIds: string[]) => void;

  validateMapping: (mapping: EnhancedFieldMapping, elementType: string) => MappingValidation;
  validateAllMappings: (elementId: string, elementType: string) => void;

  loadPreview: (
    source: SourceModule,
    tableName: string,
    filterColumn?: string,
    filterValue?: string
  ) => Promise<void>;
  setPreviewRecord: (index: number) => void;
  clearPreview: () => void;

  setSelectedSource: (source: SourceModule | null) => void;
  setSelectedTable: (table: string | null) => void;
  setSelectedColumn: (column: string | null) => void;
  setMappingPanelOpen: (open: boolean) => void;

  getMappingsForElement: (elementId: string) => EnhancedFieldMapping[];
  getPreviewValueForMapping: (mapping: EnhancedFieldMapping) => unknown;
  getCompatibleProperties: (
    elementType: string,
    columnDataType: string
  ) => MappableProperty[];
  reset: () => void;
}

const initialState: MappingStoreState & { currentProjectId: string | null } = {
  schema: null,
  schemaLoading: false,
  schemaError: null,
  lastSchemaRefresh: null,
  elementMappings: {},
  preview: null,
  selectedSource: null,
  selectedTable: null,
  selectedColumn: null,
  mappingPanelOpen: false,
  currentProjectId: null,
};

async function persistUpsert(
  projectId: string,
  elementId: string,
  mapping: EnhancedFieldMapping,
  sortOrder: number
): Promise<void> {
  try {
    const row = mappingToRow(projectId, elementId, mapping, sortOrder);
    const { error } = await supabase
      .from('cs_element_mappings')
      .upsert(
        { ...row, updated_at: new Date().toISOString() },
        { onConflict: 'project_id,element_id,target_property' }
      );
    if (error) console.warn('[mappingStore] Failed to persist mapping:', error.message);
  } catch (err) {
    console.warn('[mappingStore] Exception persisting mapping:', err);
  }
}

async function persistDelete(mappingId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('cs_element_mappings')
      .delete()
      .eq('id', mappingId);
    if (error) console.warn('[mappingStore] Failed to delete mapping:', error.message);
  } catch (err) {
    console.warn('[mappingStore] Exception deleting mapping:', err);
  }
}

async function persistDeleteForElements(projectId: string, elementIds: string[]): Promise<void> {
  if (elementIds.length === 0) return;
  try {
    const { error } = await supabase
      .from('cs_element_mappings')
      .delete()
      .eq('project_id', projectId)
      .in('element_id', elementIds);
    if (error) console.warn('[mappingStore] Failed to delete mappings for elements:', error.message);
  } catch (err) {
    console.warn('[mappingStore] Exception deleting mappings for elements:', err);
  }
}

type FullState = MappingStoreState & { currentProjectId: string | null } & MappingStoreActions;

export const useMappingStore = create<FullState>(
  (set, get) => ({
    ...initialState,

    setCurrentProject: (projectId) => {
      set({ currentProjectId: projectId });
    },

    loadMappingsForProject: async (projectId, projectElements) => {
      if (!projectId) {
        set({ currentProjectId: null, elementMappings: {} });
        return [];
      }

      set({ currentProjectId: projectId });

      try {
        const { data, error } = await supabase
          .from('cs_element_mappings')
          .select('*')
          .eq('project_id', projectId)
          .order('sort_order', { ascending: true });

        if (error) {
          console.warn('[mappingStore] Failed to load mappings:', error.message);
          set({ elementMappings: {} });
          return [];
        }

        const grouped: Record<string, EnhancedFieldMapping[]> = {};
        for (const row of (data || []) as DbRow[]) {
          const m = rowToMapping(row);
          if (!grouped[row.element_id]) grouped[row.element_id] = [];
          grouped[row.element_id].push(m);
        }

        // One-time migration: copy inline `dynamicField` into the store for any element
        // that does not yet have a persisted mapping for that target property, AND record
        // which elements still carry a legacy inline field so the caller can strip it.
        // `cs_element_mappings` is the single store-of-record for dynamic bindings.
        const migratedElementIds: string[] = [];
        if (projectElements && projectElements.length > 0) {
          const toPersist: Array<{ elementId: string; mapping: EnhancedFieldMapping; idx: number }> = [];
          for (const el of projectElements) {
            const inline = (el as unknown as { dynamicField?: {
              source: string;
              table: string;
              field: string;
              targetProperty: string;
              fallbackValue?: string;
              transform?: string;
            } }).dynamicField;
            if (!inline) continue;

            // Any element still carrying an inline field needs to have it cleared so the
            // LayerPanel and PropertyPanel agree on dynamic-vs-static state.
            migratedElementIds.push(el.id);

            const existing = grouped[el.id] || [];
            const duplicate = existing.some(
              (m) => m.targetProperty === inline.targetProperty
            );
            if (duplicate) continue;

            const migrated: EnhancedFieldMapping = {
              id: newMappingId(),
              source: (inline.source as SourceModule) || 'ad',
              tableName: inline.table,
              columnName: inline.field,
              columnDataType: 'text',
              targetProperty: inline.targetProperty as MappableProperty,
              fallbackValue: inline.fallbackValue ?? '',
              transformExpression: inline.transform ?? null,
              isValid: true,
              validationErrors: [],
              createdAt: new Date().toISOString(),
              filterColumn: null,
              filterValue: null,
              filterCategory: null,
            };
            if (!grouped[el.id]) grouped[el.id] = [];
            grouped[el.id].push(migrated);
            toPersist.push({ elementId: el.id, mapping: migrated, idx: grouped[el.id].length - 1 });
          }

          for (const item of toPersist) {
            await persistUpsert(projectId, item.elementId, item.mapping, item.idx);
          }
        }

        set({ elementMappings: grouped });
        return migratedElementIds;
      } catch (err) {
        console.warn('[mappingStore] Exception loading mappings:', err);
        set({ elementMappings: {} });
        return [];
      }
    },

    loadSchema: async (forceRefresh = false) => {
      set({ schemaLoading: true, schemaError: null });

      try {
        const result = await discoverSchema(forceRefresh);

        set({
          schema: result,
          schemaLoading: false,
          schemaError: result.error,
          lastSchemaRefresh: result.discoveredAt,
        });
      } catch (err) {
        set({
          schemaLoading: false,
          schemaError:
            err instanceof Error ? err.message : 'Failed to load schema',
        });
      }
    },

    refreshSchema: async () => {
      invalidateSchemaCache();
      await get().loadSchema(true);
    },

    addMapping: async (elementId, mappingData) => {
      const newMapping: EnhancedFieldMapping = {
        ...mappingData,
        id: newMappingId(),
        isValid: true,
        validationErrors: [],
        createdAt: new Date().toISOString(),
      };

      const projectId = get().currentProjectId;
      let displacedId: string | null = null;
      let nextIndex = 0;

      set((state) => {
        const existing = state.elementMappings[elementId] || [];

        const duplicateIdx = existing.findIndex(
          (m) => m.targetProperty === newMapping.targetProperty
        );
        if (duplicateIdx >= 0) {
          displacedId = existing[duplicateIdx].id;
          const next = existing.map((m, i) => (i === duplicateIdx ? newMapping : m));
          nextIndex = duplicateIdx;
          return {
            elementMappings: {
              ...state.elementMappings,
              [elementId]: next,
            },
          };
        }

        nextIndex = existing.length;
        return {
          elementMappings: {
            ...state.elementMappings,
            [elementId]: [...existing, newMapping],
          },
        };
      });

      if (projectId) {
        if (displacedId && displacedId !== newMapping.id) {
          await persistDelete(displacedId);
        }
        await persistUpsert(projectId, elementId, newMapping, nextIndex);
      }

      await useStudioStore.getState().ensureEditAsTable(newMapping.source, newMapping.tableName);
      ensureElementIsDynamic(elementId);
    },

    addMappingToElements: async (elementIds, mapping) => {
      if (!Array.isArray(elementIds) || elementIds.length === 0) {
        return { successCount: 0, failureCount: 0, errors: [] };
      }

      const uniqueIds = Array.from(new Set(elementIds));
      let successCount = 0;
      let failureCount = 0;
      const errors: Array<{ elementId: string; error: string }> = [];

      for (const elId of uniqueIds) {
        try {
          await get().addMapping(elId, mapping);
          successCount++;
        } catch (err) {
          failureCount++;
          errors.push({
            elementId: elId,
            error: err instanceof Error ? err.message : String(err),
          });
        }
      }

      return { successCount, failureCount, errors };
    },

    updateMapping: async (elementId, mappingId, updates) => {
      const projectId = get().currentProjectId;
      let updatedMapping: EnhancedFieldMapping | null = null;
      let sortOrder = 0;

      set((state) => {
        const mappings = state.elementMappings[elementId];
        if (!mappings) return state;

        const next = mappings.map((m, i) => {
          if (m.id !== mappingId) return m;
          const merged = { ...m, ...updates } as EnhancedFieldMapping;
          updatedMapping = merged;
          sortOrder = i;
          return merged;
        });

        return {
          elementMappings: {
            ...state.elementMappings,
            [elementId]: next,
          },
        };
      });

      const merged = updatedMapping as EnhancedFieldMapping | null;
      if (projectId && merged) {
        await persistUpsert(projectId, elementId, merged, sortOrder);
      }
      if (merged) {
        await useStudioStore.getState().ensureEditAsTable(merged.source, merged.tableName);
      }
      ensureElementIsDynamic(elementId);
    },

    removeMapping: (elementId, mappingId) => {
      set((state) => {
        const mappings = state.elementMappings[elementId];
        if (!mappings) return state;

        const filtered = mappings.filter((m) => m.id !== mappingId);
        const newMappings = { ...state.elementMappings };

        if (filtered.length === 0) {
          delete newMappings[elementId];
        } else {
          newMappings[elementId] = filtered;
        }

        return { elementMappings: newMappings };
      });

      persistDelete(mappingId);
      ensureElementIsStaticIfNoMappings(elementId);
    },

    removeAllMappings: (elementId) => {
      const projectId = get().currentProjectId;
      const existing = get().elementMappings[elementId] || [];

      set((state) => {
        const newMappings = { ...state.elementMappings };
        delete newMappings[elementId];
        return { elementMappings: newMappings };
      });

      if (projectId && existing.length > 0) {
        persistDeleteForElements(projectId, [elementId]);
      }
      ensureElementIsStaticIfNoMappings(elementId);
    },

    removeAllMappingsForElements: (elementIds) => {
      if (elementIds.length === 0) return;
      const projectId = get().currentProjectId;
      set((state) => {
        const newMappings = { ...state.elementMappings };
        for (const id of elementIds) delete newMappings[id];
        return { elementMappings: newMappings };
      });
      if (projectId) persistDeleteForElements(projectId, elementIds);
    },

    validateMapping: (mapping, elementType) => {
      const errors: string[] = [];
      const warnings: string[] = [];

      const propConfig = MAPPABLE_PROPERTIES.find(
        (p) => p.key === mapping.targetProperty
      );
      if (!propConfig) {
        errors.push(`Unknown target property: ${mapping.targetProperty}`);
      } else if (
        !propConfig.supportedElementTypes.includes(
          elementType as 'text' | 'image' | 'shape' | 'button'
        )
      ) {
        errors.push(
          `Property "${propConfig.label}" is not supported for ${elementType} elements`
        );
      }

      if (propConfig) {
        const isCompatible = propConfig.compatibleDataTypes.some(
          (dt) =>
            mapping.columnDataType.toLowerCase().includes(dt.toLowerCase()) ||
            dt.toLowerCase().includes(mapping.columnDataType.toLowerCase())
        );
        if (!isCompatible) {
          warnings.push(
            `Column type "${mapping.columnDataType}" may not be compatible with "${propConfig.label}". ` +
              `Expected: ${propConfig.compatibleDataTypes.join(', ')}`
          );
        }
      }

      if (!mapping.tableName) {
        errors.push('No source table selected');
      }
      if (!mapping.columnName) {
        errors.push('No source column selected');
      }

      if (
        mapping.targetProperty === 'textContent' &&
        !mapping.fallbackValue
      ) {
        warnings.push(
          'No fallback value set for text content. Element will be empty if data is missing.'
        );
      }

      if (
        mapping.targetProperty === 'imageUrl' &&
        !mapping.fallbackValue
      ) {
        warnings.push(
          'No fallback URL set for image. Element will show broken image if data is missing.'
        );
      }

      const hasAnyFilter = !!(
        mapping.filterColumn ||
        mapping.filterValue ||
        mapping.filterCategory
      );
      if (hasAnyFilter) {
        if (!mapping.filterColumn) {
          errors.push('EAV mapping is missing its filter column (e.g. attribute_key)');
        }
        if (!mapping.filterValue) {
          errors.push('EAV mapping is missing its filter value (the field key)');
        }
        if (!mapping.filterCategory) {
          errors.push('EAV mapping is missing its filter category');
        }
      }

      return {
        isValid: errors.length === 0,
        errors,
        warnings,
      };
    },

    validateAllMappings: (elementId, elementType) => {
      const state = get();
      const mappings = state.elementMappings[elementId];
      if (!mappings) return;

      const updatedMappings = mappings.map((mapping) => {
        const validation = state.validateMapping(mapping, elementType);
        return {
          ...mapping,
          isValid: validation.isValid,
          validationErrors: [...validation.errors, ...validation.warnings],
        };
      });

      set((prev) => ({
        elementMappings: {
          ...prev.elementMappings,
          [elementId]: updatedMappings,
        },
      }));
    },

    loadPreview: async (source, tableName, filterColumn, filterValue) => {
      set({
        preview: {
          source,
          tableName,
          records: [],
          selectedRecordIndex: 0,
          loading: true,
          error: null,
          fetchedAt: null,
        },
      });

      const { records, error } = await fetchPreviewRecords(
        tableName,
        10,
        filterColumn,
        filterValue
      );

      set({
        preview: {
          source,
          tableName,
          records,
          selectedRecordIndex: 0,
          loading: false,
          error,
          fetchedAt: new Date().toISOString(),
        },
      });
    },

    setPreviewRecord: (index) => {
      set((state) => {
        if (!state.preview) return state;
        return {
          preview: {
            ...state.preview,
            selectedRecordIndex: Math.max(
              0,
              Math.min(index, state.preview.records.length - 1)
            ),
          },
        };
      });
    },

    clearPreview: () => {
      set({ preview: null });
    },

    setSelectedSource: (source) => {
      set({ selectedSource: source, selectedTable: null, selectedColumn: null });
    },

    setSelectedTable: (table) => {
      set({ selectedTable: table, selectedColumn: null });
    },

    setSelectedColumn: (column) => {
      set({ selectedColumn: column });
    },

    setMappingPanelOpen: (open) => {
      set({ mappingPanelOpen: open });
    },

    getMappingsForElement: (elementId) => {
      return get().elementMappings[elementId] || [];
    },

    getPreviewValueForMapping: (mapping) => {
      const state = get();
      if (!state.preview) return null;
      if (state.preview.tableName !== mapping.tableName) return null;

      const record = state.preview.records[state.preview.selectedRecordIndex];
      if (!record) return null;

      const value = record[mapping.columnName];

      if (mapping.transformExpression && value != null) {
        return applyTransform(String(value), mapping.transformExpression);
      }

      return value ?? mapping.fallbackValue ?? null;
    },

    getCompatibleProperties: (elementType, columnDataType) => {
      return MAPPABLE_PROPERTIES.filter(
        (prop) =>
          prop.supportedElementTypes.includes(
            elementType as 'text' | 'image' | 'shape' | 'button'
          ) &&
          prop.compatibleDataTypes.some(
            (dt) =>
              columnDataType.toLowerCase().includes(dt.toLowerCase()) ||
              dt.toLowerCase().includes(columnDataType.toLowerCase())
          )
      ).map((prop) => prop.key);
    },

    reset: () => {
      set({ ...initialState });
    },
  })
);

function ensureElementIsDynamic(elementId: string): void {
  const studio = useStudioStore.getState();
  const element = studio.canvasState.elements.find((e) => e.id === elementId);
  if (!element || element.contentType === 'DYNAMIC') return;
  studio.updateElement(elementId, { contentType: 'DYNAMIC' } as Partial<CanvasElement>);
}

function ensureElementIsStaticIfNoMappings(elementId: string): void {
  const remaining = useMappingStore.getState().elementMappings[elementId] || [];
  if (remaining.length > 0) return;
  const studio = useStudioStore.getState();
  const element = studio.canvasState.elements.find((e) => e.id === elementId);
  if (!element || element.contentType === 'STATIC') return;
  const hasInline = !!(element as unknown as { dynamicField?: unknown }).dynamicField;
  if (hasInline) return;
  studio.updateElement(elementId, { contentType: 'STATIC' } as Partial<CanvasElement>);
}

export function applyTransform(value: string, expression: string): string {
  const parts = expression.split(':');
  const command = parts[0].toLowerCase();
  const arg = parts.slice(1).join(':');

  switch (command) {
    case 'uppercase':
      return value.toUpperCase();
    case 'lowercase':
      return value.toLowerCase();
    case 'capitalize':
      return value.replace(/\b\w/g, (char) => char.toUpperCase());
    case 'prefix':
      return `${arg}${value}`;
    case 'suffix':
      return `${value}${arg}`;
    case 'truncate': {
      const maxLen = parseInt(arg, 10);
      if (isNaN(maxLen) || value.length <= maxLen) return value;
      return value.slice(0, maxLen) + '\u2026';
    }
    default:
      return value;
  }
}
